import { matchPath } from 'react-router-dom';

// Single source of truth for which lykbl.com URLs exist, which are indexable,
// and which get prerendered to static HTML at build time (scripts/prerender.mjs).
// Keep in sync with the <Routes> table in App.tsx and with public/_redirects —
// scripts/verify-prerender.mjs fails the build when they drift.

export const SITE_URL = 'https://lykbl.com';

/** Default share image (the landing flight's first scene still). */
export const DEFAULT_OG_IMAGE =
  'https://qdvlzcqejhyceqrluzvl.supabase.co/storage/v1/object/public/marketing-flight/quiet-floors/still1.jpg';

/** Marker written to #root[data-ssr] in the prerendered 404.html. */
export const NOT_FOUND_MARKER = '__404__';

export interface PageRoute {
  /** react-router pattern, e.g. `/blog/:id`. */
  pattern: string;
  /** false → `noindex, nofollow` default and excluded from the sitemap. */
  indexable: boolean;
  /** Listed in sitemap.xml (indexable AND self-canonical). */
  sitemap: boolean;
}

export const PAGE_ROUTES: readonly PageRoute[] = [
  { pattern: '/', indexable: true, sitemap: true },
  // Self-canonical: the text-rich landing page (founder decision 2026-09-24).
  { pattern: '/home', indexable: true, sitemap: true },
  { pattern: '/about', indexable: true, sitemap: true },
  { pattern: '/help', indexable: true, sitemap: true },
  { pattern: '/privacy', indexable: true, sitemap: true },
  { pattern: '/legal/terms', indexable: true, sitemap: true },
  // Unlisted for MVP (reachable by URL, noindex) — see App.tsx.
  { pattern: '/blog', indexable: false, sitemap: false },
  { pattern: '/blog/:id', indexable: false, sitemap: false },
  { pattern: '/learn', indexable: false, sitemap: false },
  // Request-access form while the public beta is closed; a short pointer to
  // lykbl.ai/signup once it opens (lib/signup.ts). Out of search either way.
  { pattern: '/waitlist', indexable: false, sitemap: false },
  // Dawn/Night flight aliases and auth hand-off: real pages, never indexed.
  { pattern: '/flight-a', indexable: false, sitemap: false },
  { pattern: '/flight-b', indexable: false, sitemap: false },
  { pattern: '/auth', indexable: false, sitemap: false },
  { pattern: '/auth/callback', indexable: false, sitemap: false },
];

/** Client-side <Navigate> routes in App.tsx; each must also be a server
 * redirect in public/_redirects so it never falls through to the 404 page. */
export const REDIRECT_PATHS: readonly string[] = [
  '/flight',
  '/brands/waitlist',
  '/colab',
  '/campaign-connect',
  '/creators',
  '/brands',
  '/pricing',
  '/CES2026',
  '/join-creator',
  '/legal/privacy',
  '/ai-amp',
  '/careers',
  '/press',
  '/press-kit',
  '/contact',
];

/** Routes rendered by the scroll-flight engine. They are prerendered for
 * crawlers but the client replaces (never hydrates) them: the variant is
 * resolved from browser-only state, so server and client markup can differ. */
export const FLIGHT_PATHS: readonly string[] = ['/', '/flight-a', '/flight-b'];

export function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '') || '/';
  return pathname;
}

export function findPageRoute(pathname: string): PageRoute | undefined {
  const path = normalizePath(pathname);
  return PAGE_ROUTES.find((route) => matchPath({ path: route.pattern, end: true }, path) !== null);
}

/** True when the SPA router renders something other than the 404 page. */
export function isKnownPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (findPageRoute(path)) return true;
  return REDIRECT_PATHS.some((redirect) => matchPath({ path: redirect, end: true }, path) !== null);
}

export function isFlightPath(pathname: string): boolean {
  return FLIGHT_PATHS.includes(normalizePath(pathname));
}

/** Concrete (parameter-free) page URLs; blog posts are added by entry-server. */
export function staticPagePaths(): string[] {
  return PAGE_ROUTES.filter((route) => !route.pattern.includes(':')).map((route) => route.pattern);
}

export function canonicalUrl(pathname: string): string {
  const path = normalizePath(pathname);
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}
